import React from "react";

import { useReploHotkeys } from "@editor/hooks/useHotkeys";

import { useModifiers } from "@/features/react-editor/components/modifiers/hooks/useModifiers";
import {
  useDefaultSelectedElementDesignation,
  useModifiersStore,
} from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

interface ReactEditorGlobalHotkeysListenerProps {
  containerRef: React.RefObject<HTMLDivElement>;
}

const ReactEditorGlobalHotkeysListener: React.FC<
  ReactEditorGlobalHotkeysListenerProps
> = ({ containerRef }) => {
  const { displayMode } = useModifiersStore();
  const defaultDesignation = useDefaultSelectedElementDesignation();

  const { applyModifier, isLoading: isPending } = useModifiers();

  const handleDelete = React.useCallback(() => {
    const isInSelectMode = displayMode === "select";
    const isNotLoading = !isPending;
    const isNotTextElement = defaultDesignation?.element.type !== "text";

    const canDeleteElement = isInSelectMode && isNotLoading && isNotTextElement;

    if (canDeleteElement && defaultDesignation) {
      void applyModifier(
        { type: "delete" },
        defaultDesignation.element.byteOffset,
      );
    }
  }, [displayMode, defaultDesignation, isPending, applyModifier]);

  useReploHotkeys(
    {
      delete: handleDelete,
    },
    containerRef.current,
  );

  // Note (Noah, 2025-09-18): Whenever the default selected element changes, put focus on the
  // editor container so that keyboard events work
  useOnValueChange(defaultDesignation, (newDesignation, oldDesignation) => {
    if (
      oldDesignation?.element.byteOffset !==
        newDesignation?.element.byteOffset &&
      newDesignation &&
      containerRef.current
    ) {
      containerRef.current.focus();
    }
  });

  return null;
};

export default ReactEditorGlobalHotkeysListener;
